import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight, Expand, X } from "lucide-react";
import { Reveal, Eyebrow } from "./Reveal";

const WORKS = [
  { src: "/gallery/01.jpg", title: "Merle", meta: "Donkey original · wildflower meadow" },
  { src: "/gallery/02.jpg", title: "Mozie", meta: "Canine original · hand-painted" },
  { src: "/gallery/03.jpg", title: "Henry", meta: "Canine original · hand-painted" },
  { src: "/gallery/04.jpg", title: "The Beauregards", meta: "Emu duo original · hand-painted" },
  { src: "/gallery/05.jpg", title: "Hank", meta: "Canine original · periwinkle ground" },
  { src: "/gallery/06.jpg", title: "Ranger", meta: "Canine original · polka dot ground" },
  { src: "/gallery/07.jpg", title: "Endora", meta: "Emu original · macro study" },
  { src: "/gallery/08.jpg", title: "Mittens", meta: "Feline original · hot pink ground" },
  { src: "/gallery/09.jpg", title: "Bash", meta: "Canine original · electric blue study" },
  { src: "/gallery/10.jpg", title: "Bilbo", meta: "Fish original · studio signature" },
  { src: "/gallery/11.jpg", title: "Shababy", meta: "Canine original · confetti ground" },
  { src: "/gallery/12.jpg", title: "Dali", meta: "Dalmatian original · monochrome study" },
  { src: "/gallery/13.webp", title: "Maui", meta: "Canine original · sunrise palette" },
];

export const Gallery = () => {
  const [index, setIndex] = useState(0);
  const [lightbox, setLightbox] = useState(false);
  const total = WORKS.length;

  const prev = useCallback(() => setIndex((i) => (i - 1 + total) % total), [total]);
  const next = useCallback(() => setIndex((i) => (i + 1) % total), [total]);

  useEffect(() => {
    if (!lightbox) return;
    const onKey = (e) => {
      if (e.key === "Escape") setLightbox(false);
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightbox, prev, next]);

  const work = WORKS[index];

  return (
    <section id="gallery" data-testid="gallery-section" className="relative py-28 sm:py-36 bg-[#100D0A]">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="flex flex-wrap items-end justify-between gap-6 mb-14">
          <div>
            <Reveal><Eyebrow>The Gallery</Eyebrow></Reveal>
            <Reveal delay={0.1}>
              <h2 className="mt-5 font-serif tracking-tight leading-[1.08] text-3xl sm:text-4xl lg:text-5xl text-[#EAF5FA]">
                Originals from the <span className="gold-foil-text italic">easel</span>
              </h2>
            </Reveal>
          </div>
          <Reveal delay={0.2}>
            <p className="text-sm text-stone-400 max-w-sm leading-relaxed">
              Every piece below left the studio as a signed original — painted for one family, from one conversation.
            </p>
          </Reveal>
        </div>

        <Reveal delay={0.15}>
          <div className="relative spotlight rounded-3xl px-4 py-10 sm:px-12">
            <div className="relative mx-auto max-w-2xl">
              <button
                data-testid="gallery-stage"
                onClick={() => setLightbox(true)}
                className="group relative block w-full cursor-zoom-in"
                aria-label="Open artwork in lightbox"
              >
                <div className="frame-mat p-4 sm:p-6 rounded-sm">
                  <div className="relative aspect-[4/5] sm:aspect-[5/6] overflow-hidden bg-[#181512] rounded-[2px]">
                    <AnimatePresence mode="wait">
                      <motion.img
                        key={work.src}
                        src={work.src}
                        alt={work.title}
                        initial={{ opacity: 0, scale: 1.04 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.98 }}
                        transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
                        className="absolute inset-0 w-full h-full object-contain"
                      />
                    </AnimatePresence>
                  </div>
                </div>
                <span className="absolute top-8 right-8 sm:top-10 sm:right-10 p-2.5 rounded-full bg-[#0C0A09]/70 text-stone-300 opacity-0 group-hover:opacity-100 transition-opacity duration-300 border border-stone-700">
                  <Expand className="w-4 h-4" />
                </span>
              </button>

              <button
                data-testid="gallery-prev-button"
                onClick={prev}
                aria-label="Previous artwork"
                className="absolute top-1/2 -translate-y-1/2 -left-3 sm:-left-16 p-3 rounded-full border border-stone-700 text-stone-300 hover:border-[#41C2F2] hover:text-[#41C2F2] transition-colors bg-[#0C0A09]/70 backdrop-blur-sm"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <button
                data-testid="gallery-next-button"
                onClick={next}
                aria-label="Next artwork"
                className="absolute top-1/2 -translate-y-1/2 -right-3 sm:-right-16 p-3 rounded-full border border-stone-700 text-stone-300 hover:border-[#41C2F2] hover:text-[#41C2F2] transition-colors bg-[#0C0A09]/70 backdrop-blur-sm"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>

            <div className="mt-8 text-center">
              <AnimatePresence mode="wait">
                <motion.div
                  key={work.src}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.4 }}
                >
                  <p data-testid="gallery-artwork-title" className="font-serif text-xl text-[#EAF5FA]">{work.title}</p>
                  <p className="text-xs uppercase tracking-[0.2em] font-mono text-stone-500 mt-1">{work.meta}</p>
                </motion.div>
              </AnimatePresence>
              <p data-testid="gallery-counter" className="mt-3 font-mono text-xs text-[#41C2F2] tracking-[0.3em]">
                {String(index + 1).padStart(2, "0")} / {String(total).padStart(2, "0")}
              </p>
            </div>

            <div data-testid="gallery-thumbnails" className="mt-8 flex gap-3 justify-start sm:justify-center overflow-x-auto pb-2 px-1">
              {WORKS.map((w, i) => (
                <button
                  key={w.src}
                  data-testid={`gallery-thumb-${i + 1}`}
                  onClick={() => setIndex(i)}
                  aria-label={`View ${w.title}`}
                  className={`shrink-0 w-14 h-14 sm:w-16 sm:h-16 rounded-sm overflow-hidden border transition-all duration-300 ${
                    i === index ? "border-[#41C2F2] scale-105" : "border-stone-800 opacity-50 hover:opacity-90"
                  }`}
                >
                  <img src={w.src} alt="" className="w-full h-full object-cover" loading="lazy" />
                </button>
              ))}
            </div>
          </div>
        </Reveal>
      </div>

      <AnimatePresence>
        {lightbox && (
          <motion.div
            data-testid="gallery-lightbox"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[100] bg-[#0C0A09]/95 backdrop-blur-md flex flex-col items-center justify-center p-4 sm:p-10"
            onClick={() => setLightbox(false)}
          >
            <button
              data-testid="gallery-lightbox-close"
              onClick={() => setLightbox(false)}
              aria-label="Close lightbox"
              className="absolute top-5 right-5 p-3 rounded-full border border-stone-700 text-stone-300 hover:text-[#41C2F2] hover:border-[#41C2F2] transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
            <motion.img
              key={work.src}
              src={work.src}
              alt={work.title}
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
              className="max-h-[78vh] max-w-full object-contain rounded-sm shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            />
            <p className="mt-5 font-serif text-lg text-[#EAF5FA]">{work.title}</p>
            <p className="text-xs uppercase tracking-[0.2em] font-mono text-stone-500 mt-1">{work.meta}</p>
            <div className="mt-6 flex gap-4" onClick={(e) => e.stopPropagation()}>
              <button data-testid="gallery-lightbox-prev" onClick={prev} aria-label="Previous" className="p-3 rounded-full border border-stone-700 text-stone-300 hover:border-[#41C2F2] hover:text-[#41C2F2] transition-colors">
                <ChevronLeft className="w-5 h-5" />
              </button>
              <button data-testid="gallery-lightbox-next" onClick={next} aria-label="Next" className="p-3 rounded-full border border-stone-700 text-stone-300 hover:border-[#41C2F2] hover:text-[#41C2F2] transition-colors">
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};
